import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Brain, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Quiz } from '../lib/types';

interface Question {
  id: string;
  quiz_id: string | null;
  ['Question 1']: string;
  ['Q1 Option 1']: string;
  ['Q1 Option 2']: string;
  ['Q1 Option 3']: string;
  ['Q1 Option 4']: string;
  ['Q1 correct_answer']: string;
  ['Q1 Explanation']: string;
  ['Question 2']: string;
  ['Q2 Option 1']: string;
  ['Q2 Option 2']: string;
  ['Q2 Option 3']: string;
  ['Q2 Option 4']: string;
  ['Q2 correct_answer']: string;
  ['Q2 Explanation']: string;
  ['Question 3']: string;
  ['Q3 Option 1']: string;
  ['Q3 Option 2']: string;
  ['Q3 Option 3']: string;
  ['Q3 Option 4']: string;
  ['Q3 correct_answer']: string;
  ['Q3 Explanation']: string;
  ['Question 4']: string;
  ['Q4 Option 1']: string;
  ['Q4 Option 2']: string;
  ['Q4 Option 3']: string;
  ['Q4 Option 4']: string;
  ['Q4 correct_answer']: string;
  ['Q4 Explanation']: string;
  ['Question 5']: string;
  ['Q5 Option 1']: string;
  ['Q5 Option 2']: string;
  ['Q5 Option 3']: string;
  ['Q5 Option 4']: string;
  ['Q5 correct_answer']: string;
  ['Q5 Explanation']: string;
  ['Question 6']: string;
  ['Q6 Option 1']: string;
  ['Q6 Option 2']: string;
  ['Q6 Option 3']: string;
  ['Q6 Option 4']: string;
  ['Q6 correct_answer']: string;
  ['Q6 Explanation']: string;
  ['Question 7']: string;
  ['Q7 Option 1']: string;
  ['Q7 Option 2']: string;
  ['Q7 Option 3']: string;
  ['Q7 Option 4']: string;
  ['Q7 correct_answer']: string;
  ['Q7 Explanation']: string;
  ['Question 8']: string;
  ['Q8 Option 1']: string;
  ['Q8 Option 2']: string;
  ['Q8 Option 3']: string;
  ['Q8 Option 4']: string;
  ['Q8 correct_answer']: string;
  ['Q8 Explanation']: string;
  ['Question 9']: string;
  ['Q9 Option 1']: string;
  ['Q9 Option 2']: string;
  ['Q9 Option 3']: string;
  ['Q9 Option 4']: string;
  ['Q9 correct_answer']: string;
  ['Q9 Explanation']: string;
  ['Question 10']: string;
  ['Q10 Option 1']: string;
  ['Q10 Option 2']: string;
  ['Q10 Option 3']: string;
  ['Q10 Option 4']: string;
  ['Q10 correct_answer']: string;
  ['Q10 Explanation']: string;
  created_at: string;
  updated_at: string;
}

const QuizSolutionPage = () => {
  const { id } = useParams();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [question, setQuestion] = useState<Question | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        if (!id) return;

        // Fetch quiz details
        const { data: quizData, error: quizError } = await supabase
          .from('quiz')
          .select('*')
          .eq('id', id)
          .single();

        if (quizError) throw quizError;
        if (!quizData) throw new Error('Quiz non trouvé');

        setQuiz(quizData);

        // Fetch questions for this quiz
        const { data: questionsData, error: questionsError } = await supabase
          .from('questions')
          .select('*')
          .eq('quiz_id', id)
          .order('created_at', { ascending: true });

        if (questionsError) throw questionsError;
        
        if (questionsData && questionsData.length > 0) {
          setQuestion(questionsData[0]); // We only need the first record as it contains all questions
        }
      } catch (err) {
        console.error('Error fetching quiz data:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuizData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
      </div>
    );
  }

  if (error || !quiz || !question) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error || 'Quiz non trouvé'}</p>
          <Link
            to="/mes-fiches"
            className="mt-4 inline-flex items-center text-sm text-red-600 hover:text-red-500"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Retour à mes fiches
          </Link>
        </div>
      </div>
    );
  }

  // Generate an array of question numbers 1-10
  const questionNumbers = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-4 py-5 sm:px-6 bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Quiz "{quiz.title}" : La soluce</h1>
              {quiz.fiche_id && (
                <Link
                  to={`/fiche/${quiz.fiche_id}`}
                  className="text-sm text-secondary-600 hover:text-secondary-700 mt-1 inline-flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Retour à la fiche
                </Link>
              )}
            </div>
            <div className="flex space-x-3">
              <Link
                to={`/quiz/${id}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Brain className="h-4 w-4 mr-2" />
                Mode Quiz
              </Link>
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                onClick={() => window.print()}
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-5 sm:p-6">
          <div className="space-y-8">
            {questionNumbers.map(num => {
              const questionKey = `Question ${num}` as keyof Question;
              const questionText = question[questionKey];

              // Skip if question doesn't exist
              if (!questionText) return null;

              // Get options for current question
              const options = [
                question[`Q${num} Option 1` as keyof Question],
                question[`Q${num} Option 2` as keyof Question],
                question[`Q${num} Option 3` as keyof Question],
                question[`Q${num} Option 4` as keyof Question],
              ].filter((option): option is string => Boolean(option));

              const correctAnswer = question[`Q${num} correct_answer` as keyof Question];
              const explanation = question[`Q${num} Explanation` as keyof Question];

              return (
                <div key={num} className="border-b border-gray-200 pb-8 last:border-b-0">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Question {num}: {questionText}
                    </h3>
                  </div>
                  <div className="ml-4 space-y-2">
                    {options.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        className={`p-3 rounded-lg ${
                          option === correctAnswer
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <p className={`${
                          option === correctAnswer
                            ? 'text-green-700 font-medium'
                            : 'text-gray-700'
                        }`}>
                          {option}
                          {option === correctAnswer && 
                            ' ✓ (Bonne réponse)'}
                        </p>
                      </div>
                    ))}
                    {explanation && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-600 text-sm">
                          <span className="font-medium">Explication :</span> {explanation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuizSolutionPage;